/**
 * MagicBrandSwitch — the CEO-pitch reveal button.
 *
 *  Pitch flow (Steve's words):
 *    1. Walk Arran through the live app — generic ServiceSell PRO
 *    2. Tell him "I can build it RRG-branded if you wish"
 *    3. He asks "how long would that take?"
 *    4. You press the magic button -> instant reskin to RRG/Toyota
 *    5. "Already done." 🤯
 *
 *  Design constraints:
 *    - Discreet: must be findable by Steve in <1 sec, but not draw the
 *      prospect's eye before the reveal. A small bottom-left circle with
 *      a generic icon (palette / wand) — labelled only on hover.
 *    - Keyboard shortcut: Shift+Cmd+B (Shift+Ctrl+B on Windows) so Steve
 *      can flip it without moving his hands obviously.
 *    - Cycles: default -> toyota -> rrg -> default (Arran authorised RRG
 *      name/data for the demo; ServiceSell PRO is the neutral home brand
 *      so Steve can demonstrate the full transform live on the projector)
 *    - Only rendered in the post-login app (mounted at the top of
 *      Dashboard / Director routes).
 */
import { useEffect, useState } from "react";
import { Wand2 } from "lucide-react";
import { BRAND_PROFILES, getActiveBrand, setActiveBrand } from "@/lib/brandProfile";

const ORDER = ["default", "toyota", "rrg"];

function nextBrand(currentKey) {
  const i = ORDER.indexOf(currentKey);
  return ORDER[(i + 1) % ORDER.length];
}

export default function MagicBrandSwitch() {
  const [brand, setBrand] = useState(() => getActiveBrand());
  const [hint, setHint] = useState(false);

  // Sync if another tab / pill / shortcut changes it
  useEffect(() => {
    const refresh = () => setBrand(getActiveBrand());
    window.addEventListener("storage", refresh);
    window.addEventListener("ssp-brand-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("ssp-brand-changed", refresh);
    };
  }, []);

  const trigger = () => {
    const next = nextBrand(brand.key);
    setActiveBrand(next);
    setBrand(BRAND_PROFILES[next]);
    window.dispatchEvent(new Event("ssp-brand-changed"));
    // brief hint flash so Steve sees confirmation
    setHint(true);
    setTimeout(() => setHint(false), 1400);
  };

  // Keyboard shortcut: Shift + Cmd/Ctrl + B
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        trigger();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // `trigger` reads the current brand; re-binding on brand.key keeps it fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.key]);

  const isActiveSkin = brand.key !== "default";

  return (
    <>
      {/* Floating switch — bottom-left, sized for iPad touch (48x48px) but
          kept dim/subtle so the prospect's eye doesn't go there. */}
      <button
        onClick={trigger}
        title="Brand demo switch · Shift+Cmd+B or long-press the logo"
        className={`fixed bottom-3 left-3 z-[60] w-12 h-12 rounded-full grid place-items-center transition-all touch-manipulation
          ${isActiveSkin
            ? "bg-black/90 ring-2 shadow-2xl"
            : "bg-black/40 ring-1 ring-white/15 hover:bg-black/70 active:bg-black/90"}
        `}
        style={isActiveSkin ? { boxShadow: `0 0 24px ${brand.logoTextColor}40` } : undefined}
        data-testid="magic-brand-switch"
      >
        <Wand2
          className="w-5 h-5 transition-colors"
          style={{ color: isActiveSkin ? brand.logoTextColor : "rgba(255,255,255,0.45)" }}
        />
      </button>

      {/* Confirmation hint — flashes for ~1.4s after a brand change so
          Steve sees that the click registered without having to look at the
          rest of the UI. Disappears so it doesn't distract Arran. */}
      {hint && (
        <div
          className="fixed bottom-16 left-3 z-[60] px-3 py-1.5 rounded-md bg-black/90 ring-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-2xl animate-fade-in"
          style={{ borderColor: brand.logoTextColor }}
          data-testid="magic-brand-hint"
        >
          <span className="opacity-60">Brand → </span>
          <span style={{ color: brand.logoTextColor }}>{brand.dealerName}</span>
        </div>
      )}
    </>
  );
}
